import { TrophyKey, TrophyTier } from "MVVM/@types/trophies";

// Sourced from the "Gaming badge system design" Claude Design export
// (Badges.dc.html). Each path array is stroke-drawn (fill: none) inside a
// 0-0 100x100 viewBox, matching the ring/disk markup in TrophyBadge. Swap
// individual entries here to change a badge's glyph without touching any
// rendering code.
export const TROPHY_ICON_PATHS: Record<TrophyKey, string[]> = {
  [TrophyKey.REF_QUALIFIED]: [
    "M44 43a9 9 0 1 0 0-18a9 9 0 1 0 0 18",
    "M27 70v-8c0-10 8-15 17-15s17 5 17 15v8",
    "M63 38h16M71 30v16",
  ],
  [TrophyKey.SOC_TOTAL_VIEWS]: [
    "M22 40s10-13 28-13s28 13 28 13s-10 13-28 13S22 40 22 40z",
    "M50 46a6 6 0 1 0 0-12a6 6 0 1 0 0 12",
    "M34 70a4 4 0 1 0 0-8a4 4 0 1 0 0 8M50 74a4 4 0 1 0 0-8a4 4 0 1 0 0 8M66 70a4 4 0 1 0 0-8a4 4 0 1 0 0 8",
  ],
  [TrophyKey.COMP_PARTICIPATE]: [
    "M50 36a14 14 0 1 0 0 28a14 14 0 1 0 0-28z",
    "M50 24v8M50 68v8M24 50h8M68 50h8",
    "M50 48.5a1.5 1.5 0 1 0 0 3a1.5 1.5 0 1 0 0-3z",
  ],
  [TrophyKey.COMP_PLACE]: [
    "M24 72h52",
    "M32 72V56h12",
    "M44 72V44h12v28",
    "M68 72V62H56",
    "M50 26l2.4 5 5.6.8-4 3.9.9 5.6-4.9-2.6-4.9 2.6.9-5.6-4-3.9 5.6-.8z",
  ],
  [TrophyKey.SOC_FOLLOWING]: [
    "M40 37a13 13 0 1 0 0 26a13 13 0 1 0 0-26z",
    "M60 37a13 13 0 1 0 0 26a13 13 0 1 0 0-26z",
  ],
  [TrophyKey.SOC_FOLLOWERS]: [
    "M30 44v12h8l18 10V34L38 44z",
    "M63 43a9 9 0 0 1 0 14",
    "M34 56l4 12h7l-3-12",
  ],
  [TrophyKey.SOC_CLIP_VIEWS]: [
    "M50 44a6 6 0 1 0 0 12a6 6 0 1 0 0-12z",
    "M45 46l-11-9M55 46l11-9M50 56v14",
    "M30 34a4 4 0 1 0 0 .1zM70 34a4 4 0 1 0 0 .1zM50 74a4 4 0 1 0 0 .1z",
  ],
  [TrophyKey.SOC_CLIP_LIKES]: [
    "M50 70C34 59 27 51 27 42a11.5 11.5 0 0 1 23-1a11.5 11.5 0 0 1 23 1c0 9-7 17-23 28z",
    "M72 28v6M69 31h6",
  ],
  [TrophyKey.STRK_LOGIN]: [
    "M28 36h44v34a2 2 0 0 1-2 2H30a2 2 0 0 1-2-2z",
    "M28 46h44",
    "M40 28v10M60 28v10",
    "M41 58l6 6l12-12",
  ],
  [TrophyKey.STRK_CLIP]: [
    "M66 50a16 16 0 1 1-5-11.6",
    "M58 32h8v8",
    "M46 43v14l11-7z",
  ],
  [TrophyKey.CRT_CLIPS]: [
    "M36 56a6 6 0 1 0 0 12a6 6 0 1 0 0-12z",
    "M64 56a6 6 0 1 0 0 12a6 6 0 1 0 0-12z",
    "M40.5 57L62 30",
    "M59.5 57L38 30",
  ],
  [TrophyKey.CRT_MONTAGES]: [
    "M26 34h48v32H26z",
    "M26 42h48M26 58h48",
    "M38 34v8M50 34v8M62 34v8M38 58v8M50 58v8M62 58v8",
  ],
  // TODO: placeholders until these three get their own art.
  [TrophyKey.STRK_DAILY_CLAIM]: [
    "M28 36h44v34a2 2 0 0 1-2 2H30a2 2 0 0 1-2-2z",
    "M28 46h44",
    "M40 28v10M60 28v10",
    "M41 58l6 6l12-12",
  ],
  [TrophyKey.CRT_COMMAND]: [
    "M28 30h44v30H48l-12 10V60h-8z",
    "M50 37v11M50 53v1",
  ],
  [TrophyKey.CRT_AUTOCAPTURE]: [
    "M66 50a16 16 0 1 1-5-11.6",
    "M58 32h8v8",
    "M46 43v14l11-7z",
  ],
};

// Fallback glyph for a key that doesn't match any of the above (per the
// Trophy type's contract, category/name come back null in that case too).
export const DEFAULT_TROPHY_ICON_PATHS =
  TROPHY_ICON_PATHS[TrophyKey.COMP_PLACE];

// Same source file's tier metals — `color` drives the ring/glyph/border,
// `deep` is the disk fill behind the glyph.
export const TROPHY_TIER_PALETTE: Record<
  TrophyTier,
  { color: string; deep: string }
> = {
  [TrophyTier.BRONZE]: { color: "#d98a4c", deep: "#3a2416" },
  [TrophyTier.SILVER]: { color: "#c9d0e0", deep: "#30343f" },
  [TrophyTier.GOLD]: { color: "#efc24f", deep: "#3e2f0f" },
  [TrophyTier.PLATINUM]: { color: "#9fe3e6", deep: "#14383f" },
  /* Brilliant ice-blue rather than the lavender it was: it has to read as a
     step above PLATINUM's muted teal, and purple is the one hue a diamond
     never reads as. */
  [TrophyTier.DIAMOND]: { color: "#6fd3ff", deep: "#122a57" },
};

// Locked (not yet earned) badges use this flat gray set instead of a tier.
export const TROPHY_LOCKED_PALETTE = {
  color: "#595d6c",
  deep: "#232532",
  track: "#292b31",
};

// Ring track color behind the progress arc for an earned (unlocked) badge.
export const TROPHY_TRACK_COLOR = "#3f424d";
